// src/pages/PurchasePage.js
import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, formatCurrency, Badge, PageHeader } from '../components/ui';
import { Calculator, Plus, RefreshCw } from 'lucide-react';

const initialForm = {
  customer_id: '', script_id: '', trade_date: new Date().toISOString().split('T')[0],
  lot_size: 1, quantity: '', buy_price: '', exchange: 'NSE', segment: 'EQ', remarks: ''
};

export default function PurchasePage() {
  const [form, setForm] = useState(initialForm);
  const [customers, setCustomers] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [charges, setCharges] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [broker, setBroker] = useState(null);

  useEffect(() => {
    api.get('/masters/customers').then(r => setCustomers(r.data));
    api.get('/masters/scripts').then(r => setScripts(r.data));
    loadPurchases();
  }, []);

  const loadPurchases = () => {
    api.get('/trading/purchases').then(r => setPurchases(r.data));
  };

  // Auto-calculate charges when qty/price/customer changes
  useEffect(() => {
    if (form.customer_id && form.quantity && form.buy_price) {
      const cust = customers.find(c => c.id === parseInt(form.customer_id));
      if (cust?.broker_id) {
        api.get('/trading/preview-charges', {
          params: { broker_id: cust.broker_id, quantity: form.quantity, price: form.buy_price, type: 'buy' }
        }).then(r => { setCharges(r.data); setBroker(cust); }).catch(() => {});
      }
    } else { setCharges(null); }
  }, [form.customer_id, form.quantity, form.buy_price, customers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.script_id || !form.quantity || !form.buy_price) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await api.post('/trading/purchases', { ...form, quantity: parseInt(form.quantity), buy_price: parseFloat(form.buy_price), lot_size: parseInt(form.lot_size) });
      toast.success('Purchase recorded successfully!');
      setForm(initialForm);
      setCharges(null);
      setShowForm(false);
      loadPurchases();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record purchase');
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const totalLots = form.lot_size && form.quantity ? (parseInt(form.quantity) / parseInt(form.lot_size)).toFixed(2) : 0;

  return (
    <div>
      <PageHeader
        title="Purchase Transactions"
        subtitle="Record new buy orders"
        action={<Btn onClick={() => setShowForm(!showForm)} icon={Plus}><Plus size={16} /> New Purchase</Btn>}
      />

      {/* Form */}
      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title="New Purchase Entry" subtitle="Enter trade details and review brokerage charges" />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
              <Select label="Customer *" value={form.customer_id} onChange={set('customer_id')}>
                <option value="">Select Customer</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.customer_code} - {c.customer_name}</option>)}
              </Select>
              <Select label="Script *" value={form.script_id} onChange={set('script_id')}>
                <option value="">Select Script</option>
                {scripts.map(s => <option key={s.id} value={s.id}>[{s.exchange_code}] {s.script_code} - {s.script_name}</option>)}
              </Select>
              <Input label="Trade Date *" type="date" value={form.trade_date} onChange={set('trade_date')} />
              <Input label="Lot Size" type="number" min="1" value={form.lot_size} onChange={set('lot_size')} />
              <Input label="Quantity *" type="number" min="1" value={form.quantity} onChange={set('quantity')} placeholder="e.g. 100" />
              <Input label="Buy Price (₹) *" type="number" step="0.01" min="0.01" value={form.buy_price} onChange={set('buy_price')} placeholder="e.g. 250.50" />
              <Select label="Exchange" value={form.exchange} onChange={set('exchange')}>
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </Select>
              <Select label="Segment" value={form.segment} onChange={set('segment')}>
                <option value="EQ">Equity (EQ)</option>
                <option value="FO">F&O</option>
                <option value="CDS">Currency</option>
              </Select>
              <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Optional" />
            </div>

            {/* Lot summary */}
            {form.quantity && form.lot_size && (
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, color: '#0369a1' }}>
                  <strong>Total Lots:</strong> {totalLots} ({form.quantity} qty ÷ {form.lot_size} lot size)
                </div>
                {form.buy_price && <div style={{ fontSize: 13, color: '#0369a1' }}>
                  <strong>Gross Amount:</strong> {formatCurrency(parseInt(form.quantity) * parseFloat(form.buy_price))}
                </div>}
              </div>
            )}

            {/* Charges breakdown */}
            {charges && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <Calculator size={16} color="#3b82f6" />
                  <strong style={{ fontSize: 14, color: '#0f172a' }}>Brokerage Charges Breakdown</strong>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {[
                    ['Gross Amount', charges.gross_amount],
                    ['Brokerage', charges.brokerage_amount],
                    ['STT (Buy)', charges.stt_amount],
                    ['Txn Charges', charges.transaction_charges],
                    ['GST (18%)', charges.gst_amount],
                    ['SEBI Charges', charges.sebi_charges],
                    ['Stamp Duty', charges.stamp_duty],
                    ['Total Charges', charges.total_charges],
                  ].map(([label, val]) => (
                    <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500, marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{formatCurrency(val)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14, padding: '12px 16px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>Net Amount Payable</span>
                  <span style={{ color: '#fff', fontWeight: 800, fontSize: 20 }}>{formatCurrency(charges.net_amount)}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" variant="success" disabled={loading}>{loading ? 'Saving...' : 'Record Purchase'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowForm(false); setForm(initialForm); setCharges(null); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Transactions list */}
      <Card>
        <CardHeader
          title="Purchase History"
          subtitle={`${purchases.length} transactions`}
          action={<Btn variant="ghost" size="sm" onClick={loadPurchases}><RefreshCw size={14} /></Btn>}
        />
        <Table
          headers={['Txn No', 'Date', 'Customer', 'Script', 'Exchange', 'Lots', 'Qty', 'Price', 'Gross', 'Charges', 'Net Amount']}
          rows={purchases.map(p => [
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.transaction_no}</span>,
            p.trade_date?.split('T')[0],
            <div><div style={{ fontWeight: 600 }}>{p.customer_code}</div><div style={{ fontSize: 12, color: '#64748b' }}>{p.customer_name}</div></div>,
            <div><div style={{ fontWeight: 600 }}>{p.script_code}</div><div style={{ fontSize: 12, color: '#64748b' }}>{p.script_name}</div></div>,
            <Badge color="blue">{p.exchange_code}</Badge>,
            parseFloat(p.total_lots || 0).toFixed(2),
            p.quantity,
            formatCurrency(p.buy_price),
            formatCurrency(p.gross_amount),
            formatCurrency(p.total_charges),
            <strong style={{ color: '#0f172a' }}>{formatCurrency(p.net_amount)}</strong>
          ])}
          emptyMessage="No purchase transactions yet. Click 'New Purchase' to add one."
        />
      </Card>
    </div>
  );
}
